/**
 * The category tree next to the customer card's document list.
 *
 * Only the branches this customer has files in are rendered (see
 * build-document-tree.ts); picking a branch narrows the list to that category
 * and everything under it.
 */
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronDown, ChevronRight, Folder, Files as FilesIcon } from "lucide-react";
import useRestorableState from "hooks/useRestorableState";
import {
  ALL_NODE_ID,
  DocumentTree,
  DocumentTreeNode,
  UNCATEGORIZED_NODE_ID,
  findAncestorIds,
} from "./build-document-tree";
import { collapseWhitespace, splitCategoryTitle } from "./category-title";

interface Props {
  tree: DocumentTree;
  selectedId: string;
  onSelect: (id: string) => void;
}

interface RowProps {
  title: string;
  // Category titles start with a hand-typed number ("3.1.5.1."), shown muted
  // before the name; the "all" and "uncategorized" rows have none.
  numbered?: boolean;
  count: number;
  depth: number;
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  toggleLabel?: string;
}

const TreeRow: React.FC<RowProps> = ({
  title,
  numbered = false,
  count,
  depth,
  selected,
  onSelect,
  icon,
  expandable = false,
  expanded = false,
  onToggle,
  toggleLabel,
}) => {
  const collapsedTitle = collapseWhitespace(title);
  const { number, name } = numbered
    ? splitCategoryTitle(title)
    : { number: "", name: collapsedTitle };

  // Long names wrap to two lines, so the chevron, icon and count are pinned to
  // the first line (each is one 20px line tall, like the text). Below xl the
  // tree is the touch drawer (see customer-documents-section.jsx), where the
  // chevron and the row are 44px tall.
  return (
    <div
      className="flex items-start gap-0.5"
      style={{ paddingLeft: depth * 12 }}
    >
      {expandable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={toggleLabel}
          aria-expanded={expanded}
          className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 max-xl:mt-0 max-xl:h-11 max-xl:w-11"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
      ) : (
        <span className="h-5 w-5 shrink-0 max-xl:w-11" />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        className={clsx(
          "flex min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-1 text-left text-sm max-xl:py-3",
          selected
            ? "bg-indigo-50 font-medium text-indigo-700"
            : "text-gray-700 hover:bg-gray-50"
        )}
      >
        <span
          className={clsx(
            "mt-0.5 shrink-0",
            selected ? "text-indigo-500" : "text-gray-400"
          )}
        >
          {icon}
        </span>
        {number && (
          <span className="shrink-0 text-[11px] leading-5 tabular-nums text-gray-400">
            {number}
          </span>
        )}
        <span
          className="line-clamp-2 min-w-0 break-words leading-5"
          title={collapsedTitle}
        >
          {name}
        </span>
        <span
          className={clsx(
            "ml-auto shrink-0 text-xs leading-5",
            selected ? "text-indigo-500" : "text-gray-400"
          )}
        >
          {count}
        </span>
      </button>
    </div>
  );
};

const CustomerDocumentTree: React.FC<Props> = ({
  tree,
  selectedId,
  onSelect,
}) => {
  const { t } = useTranslation();

  // Branches start open — the tree only holds branches that have documents, so
  // there is nothing to hide by default. Only what the user closed is stored.
  const [collapsedIds, setCollapsedIds] = useRestorableState<string[]>(
    "customer-documents-collapsed",
    []
  );
  const collapsed = useMemo(() => new Set(collapsedIds), [collapsedIds]);

  const toggle = useCallback(
    (id: string) => {
      setCollapsedIds((previous) =>
        previous.includes(id)
          ? previous.filter((item) => item !== id)
          : [...previous, id]
      );
    },
    [setCollapsedIds]
  );

  // Selecting a category from elsewhere (a restored selection, for instance)
  // must not leave it hidden inside a closed branch.
  useEffect(() => {
    if (!selectedId || selectedId === UNCATEGORIZED_NODE_ID) return;
    const ancestors = findAncestorIds(tree.nodes, selectedId);
    if (!ancestors || ancestors.length === 0) return;
    setCollapsedIds((previous) =>
      previous.some((id) => ancestors.includes(id))
        ? previous.filter((id) => !ancestors.includes(id))
        : previous
    );
  }, [selectedId, tree, setCollapsedIds]);

  const renderNodes = (nodes: DocumentTreeNode[], depth: number) =>
    nodes.map((node) => {
      const isExpanded = !collapsed.has(node.id);
      return (
        <div key={node.id}>
          <TreeRow
            title={node.title}
            numbered
            count={node.count}
            depth={depth}
            selected={selectedId === node.id}
            onSelect={() => onSelect(node.id)}
            icon={<Folder className="h-4 w-4" />}
            expandable={node.children.length > 0}
            expanded={isExpanded}
            onToggle={() => toggle(node.id)}
            toggleLabel={isExpanded ? t("collapse") : t("expand")}
          />
          {node.children.length > 0 && isExpanded && (
            <div>{renderNodes(node.children, depth + 1)}</div>
          )}
        </div>
      );
    });

  return (
    <nav
      aria-label={t("category")}
      className="max-h-[28rem] space-y-0.5 overflow-y-auto pr-1"
    >
      <TreeRow
        title={t("customer_documents_all")}
        count={tree.total}
        depth={0}
        selected={selectedId === ALL_NODE_ID}
        onSelect={() => onSelect(ALL_NODE_ID)}
        icon={<FilesIcon className="h-4 w-4" />}
      />
      {renderNodes(tree.nodes, 0)}
      {tree.uncategorizedFileIds.length > 0 && (
        <TreeRow
          title={t("customer_documents_uncategorized")}
          count={tree.uncategorizedFileIds.length}
          depth={0}
          selected={selectedId === UNCATEGORIZED_NODE_ID}
          onSelect={() => onSelect(UNCATEGORIZED_NODE_ID)}
          icon={<Folder className="h-4 w-4" />}
        />
      )}
    </nav>
  );
};

export default CustomerDocumentTree;
